import { useState, useEffect, useRef, FormEvent, ChangeEvent } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSupabaseAuthClient } from '../lib/supabaseAuth';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import './ordering/order-management/procurement-ui/polaris.css';
import './Login.css';

interface PasskeyExchangeResponse {
  access_token?: string;
  detail?: string | { message?: string };
}

const PASSKEY_SILENT_ERROR_NAMES = new Set(['AbortError', 'NotAllowedError']);
const PASSKEY_ENROLL_HINT =
  "Couldn't sign in with a passkey. If you haven't set one up on this device yet, "
  + 'sign in with your password and add one under Settings → Passkeys.';

function Login() {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isPasskeyLoading, setIsPasskeyLoading] = useState<boolean>(false);
  const auth = useAuth();
  const navigate = useNavigate();

  const supabaseAuthClient = getSupabaseAuthClient();
  const canUsePasskey = !!supabaseAuthClient && typeof window !== 'undefined' && !!window.PublicKeyCredential;

  // `silent` suppresses error messaging for the automatic on-mount attempt —
  // a user who just opened the login page and has no passkey shouldn't see an
  // error they didn't ask for. The manual button passes silent=false.
  const handlePasskeySignIn = async (silent = false) => {
    if (!supabaseAuthClient) return;

    setError('');
    setIsPasskeyLoading(true);

    try {
      const { data, error: passkeyError } = await supabaseAuthClient.auth.signInWithPasskey();

      if (passkeyError) {
        if (!silent && !PASSKEY_SILENT_ERROR_NAMES.has(passkeyError.name)) {
          setError(PASSKEY_ENROLL_HINT);
        }
        return;
      }

      const accessToken = data?.session?.access_token;
      if (!accessToken) {
        if (!silent) setError(PASSKEY_ENROLL_HINT);
        return;
      }

      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/auth/passkey/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: accessToken }),
      });
      const json = await response.json().catch(() => ({})) as PasskeyExchangeResponse;

      if (response.ok && json.access_token) {
        auth.setAuthToken(json.access_token);
        navigate('/', { state: { fromLogin: true } });
      } else if (!silent) {
        const detail = typeof json.detail === 'object' ? json.detail?.message : json.detail;
        setError(detail || PASSKEY_ENROLL_HINT);
      }
    } catch (err) {
      console.error('[Login] Passkey sign-in failed', err);
      if (!silent) setError(PASSKEY_ENROLL_HINT);
    } finally {
      // Never let the Supabase session outlive the ceremony — it's only a
      // stepping stone to the ops JWT exchanged above.
      await supabaseAuthClient.auth.signOut();
      setIsPasskeyLoading(false);
    }
  };

  // On arrival at the login page, immediately offer the passkey prompt so a
  // user with a passkey can sign in without touching the password form. Runs
  // once; failures (no passkey / cancelled) are silent and fall through to the
  // password form.
  const autoPasskeyAttempted = useRef(false);
  useEffect(() => {
    if (autoPasskeyAttempted.current || !canUsePasskey) return;
    autoPasskeyAttempted.current = true;
    void handlePasskeySignIn(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUsePasskey]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('[Login] Form submitted', { username });

    if (!auth) {
      console.error('[Login] No auth context available');
      return;
    }

    console.log('[Login] Calling auth.login...');
    const result = await auth.login(username, password);
    console.log('[Login] Login result:', result);

    if (result.success) {
      console.log('[Login] Login successful, navigating...');
      if (result.password_expired) {
        navigate('/change-password', {
          state: {
            fromLogin: true,
            message: result.message || 'Your password has expired. Please change it now.',
          },
        });
      } else {
        navigate('/', { state: { fromLogin: true } });
      }
    } else {
      console.log('[Login] Login failed:', result.error);
      if (result.error && result.error.includes('expired')) {
        setError('Your password has expired. Please contact an administrator to reset it.');
      } else if (result.error && result.error.includes('deactivated')) {
        setError('Your account has been deactivated. Please contact an administrator.');
      } else {
        setError(result.error || 'Invalid username or password.');
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1 className="login-header">Lytheron</h1>
        <p className="login-subtitle">Sign in to continue</p>
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="field__label" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              className="field__input"
              value={username}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setUsername(e.target.value);
                if (error) setError('');
              }}
              required
              autoComplete="username"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="field__input"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              required
              autoComplete="current-password"
            />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn btn--primary login-submit">
            Login
          </button>
        </form>
        {canUsePasskey && (
          <>
            <div className="login-divider">
              <span>or</span>
            </div>
            <button
              type="button"
              className="btn login-submit"
              onClick={() => handlePasskeySignIn(false)}
              disabled={isPasskeyLoading}
            >
              {isPasskeyLoading ? 'Waiting for passkey...' : 'Sign in with a passkey'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
